import type Phaser from 'phaser';
import type { ImageLevel } from '../levels/types';
import { fadeToScene } from '../ui/sceneTransition';
import { loadLevel, LEVEL_COUNT } from '../levels/index';

/**
 * Typed data passed to `GameScene` via `scene.start('GameScene', data)`.
 *
 * Level number is always required. The full level data is pre-loaded
 * asynchronously before the scene starts.
 */
export interface GameSceneLaunchData {
  /** 1-based level number. */
  level: number;
  /** Override the level's default queue lane count (3-6). */
  queueOverride?: number;
  /** Pre-loaded level data (loaded async before scene start). */
  levelData?: ImageLevel;
}

/**
 * Standard fade-out → scene.start transition used by every launch site.
 * Pre-loads the level data during the fade, then starts GameScene.
 */
export function launchGameScene(scene: Phaser.Scene, data: GameSceneLaunchData): void {
  // Start loading level data immediately (parallel with fade animation)
  const levelIndex = data.level - 1;
  const loadPromise = (levelIndex >= 0 && levelIndex < LEVEL_COUNT && !data.levelData)
    ? loadLevel(levelIndex)
    : Promise.resolve(data.levelData);

  fadeToScene(scene, async () => {
    // Wait for level data to be ready
    const levelData = await loadPromise;

    // Stop any sleeping or paused GameScene to avoid stale state
    if (scene.scene.isSleeping('GameScene') || scene.scene.isPaused('GameScene')) {
      scene.scene.stop('GameScene');
    }
    scene.scene.start('GameScene', { ...data, levelData });
  });
}
